/**
 * FounderIntelligence — Universal Upload Hub V1
 * (Feb 10, 2026). Admin-only inventory view over the
 * `ndg_intelligence_contributions` ledger.
 *
 * V1 shows truthful operational awareness only — no scoring,
 * no maturity model, no recommendations. Those remain
 * deferred tranches.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Inbox } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function FounderIntelligence() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/api/founder-intelligence/inventory`, { withCredentials: true });
        setData(r.data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Unable to load intelligence inventory.");
      }
    })();
  }, []);

  if (err) return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <p data-testid="fi-error" className="text-sm text-amber-300">{err}</p>
    </div>
  );
  if (!data) return (
    <div className="max-w-2xl mx-auto px-4 py-8">
      <p data-testid="fi-loading" className="text-sm text-slate-400">Reading the inventory…</p>
    </div>
  );

  const { areas, recent, totals } = data;

  return (
    <div data-testid="founder-intelligence-view" className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <header className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-500/15 flex items-center justify-center">
          <Sparkles className="w-5 h-5 text-amber-300" />
        </div>
        <div>
          <p className="text-lg font-extrabold text-white leading-tight">Founder Intelligence · Observation</p>
          <p className="text-xs text-slate-400 leading-tight">How NDG's educational understanding is beginning to develop.</p>
        </div>
      </header>

      <section className="grid grid-cols-3 gap-2">
        <Stat label="Contributions"  value={totals.contribution_count} testid="fi-total-contributions" />
        <Stat label="Areas covered"  value={`${totals.diversity} / ${totals.total_registered_areas}`} testid="fi-diversity" />
        <Stat label="Registered"     value={totals.total_registered_areas} testid="fi-registered" />
      </section>

      {/* Emerging educational themes — last-30-day activity.
          A truthful "beginning to emerge" observation, not a
          score. Silent when nothing has landed in the window. */}
      {data.emerging?.length > 0 && (
        <section>
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
            Beginning to emerge
          </h2>
          <p className="text-[11px] text-slate-500 mb-2 leading-snug">
            Areas receiving contributions in the last 30 days.
          </p>
          <div data-testid="fi-emerging" className="space-y-1.5">
            {data.emerging.map((e) => (
              <div
                key={e.key}
                data-testid={`fi-emerging-${e.key}`}
                className="flex items-center justify-between rounded-xl px-3 py-2 border border-amber-800/40 bg-amber-900/15 text-amber-100"
              >
                <p className="text-sm font-bold">{e.label}</p>
                <p className="text-xs">{e.recent_contributions} recent</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Evidence remains limited — areas with ≤1 contribution.
          Observation-shaped, not a criticism. */}
      {data.evidence_limited?.length > 0 && (
        <section>
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
            Where evidence remains limited
          </h2>
          <p className="text-[11px] text-slate-500 mb-2 leading-snug">
            These areas would benefit from further evidence. No score, no ranking — just what NDG has seen so far.
          </p>
          <div data-testid="fi-evidence-limited" className="grid grid-cols-2 gap-1.5">
            {data.evidence_limited.map((e) => (
              <div
                key={e.key}
                data-testid={`fi-limited-${e.key}`}
                className="rounded-xl px-3 py-2 border border-slate-700 bg-slate-800/40"
              >
                <p className="text-sm text-slate-200 leading-tight">{e.label}</p>
                <p className="text-[11px] text-slate-500">
                  {e.contribution_count === 0 ? "no evidence yet" : "1 contribution"}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Educational relationships beginning to appear — pairs
          of areas linked through shared contributors. No text
          mining, no LLM — the same instructor legitimately sees
          both areas as related. */}
      {data.relationships?.length > 0 && (
        <section>
          <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
            Relationships beginning to appear
          </h2>
          <p className="text-[11px] text-slate-500 mb-2 leading-snug">
            Areas that share at least one contributor.
          </p>
          <div data-testid="fi-relationships" className="space-y-1.5">
            {data.relationships.map((r, i) => (
              <div
                key={`${r.left_key}-${r.right_key}`}
                data-testid={`fi-relationship-${i}`}
                className="flex items-center justify-between rounded-xl px-3 py-2 border border-indigo-800/40 bg-indigo-900/15 text-indigo-100"
              >
                <p className="text-sm font-bold leading-tight">
                  {r.left_label} <span className="text-indigo-300">·</span> {r.right_label}
                </p>
                <p className="text-xs text-indigo-300">
                  {r.shared_contributors} shared
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      <section>
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Educational areas</h2>
        <div data-testid="fi-areas" className="space-y-1.5">
          {areas.map((a) => (
            <div
              key={a.key}
              data-testid={`fi-area-${a.key}`}
              className={`flex items-center justify-between rounded-xl px-3 py-2 border ${
                a.contribution_count > 0
                  ? "bg-emerald-900/15 border-emerald-800/40 text-emerald-100"
                  : "bg-slate-800/40 border-slate-700 text-slate-400"
              }`}
            >
              <p className="text-sm font-bold">{a.label}</p>
              <p className="text-xs">{a.contribution_count} contribution{a.contribution_count === 1 ? "" : "s"}</p>
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Recent contributions</h2>
        {recent.length === 0 ? (
          <div className="flex items-center gap-2 text-sm text-slate-400 bg-slate-800/40 border border-slate-700 rounded-xl px-3 py-3">
            <Inbox className="w-4 h-4" />
            <span>No contributions yet — start by uploading with the NDG Intelligence destination.</span>
          </div>
        ) : (
          <div data-testid="fi-recent" className="space-y-1.5">
            {recent.map((r) => (
              <div key={r.id} className="rounded-xl px-3 py-2 border border-slate-700 bg-slate-900/60">
                <p className="text-sm font-bold text-white">
                  {areas.find((a) => a.key === r.educational_area)?.label || r.educational_area}
                </p>
                <p className="text-xs text-slate-400 leading-snug">{r.intelligence_help}</p>
                <p className="text-[10px] text-slate-500 mt-1">{r.recorded_at}</p>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Stat({ label, value, testid }) {
  return (
    <div className="rounded-xl bg-slate-800 border border-slate-700 px-3 py-3">
      <p className="text-[10px] uppercase tracking-widest text-slate-500">{label}</p>
      <p data-testid={testid} className="text-2xl font-extrabold text-white mt-1">{value}</p>
    </div>
  );
}
